/**
 * InfoModuleManagement – Admin erstellt/bearbeitet/archiviert Info-Module.
 * Zugänglich über /admin/info/manage (nur Admins).
 */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  FileText, Table2, Plus, Pencil, Archive, ArchiveX, ChevronUp, ChevronDown,
  Trash2, Settings, Info, Lock, Zap, Flame, Droplets, FlaskConical, Loader2, Tag,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const ICON_OPTIONS = [
  { value: 'FileText', label: 'Dokument', Icon: FileText },
  { value: 'Table2', label: 'Tabelle', Icon: Table2 },
  { value: 'Info', label: 'Info', Icon: Info },
  { value: 'Settings', label: 'Einstellungen', Icon: Settings },
];
const COL_TYPES = ['text', 'number', 'date', 'select'];

const emptyModule = {
  name: '', type: 'pdf', icon: 'FileText',
  visible_for_all_locations: true, location_ids: [],
  employee_can_add: true, employee_can_delete_own: false,
  table_schema: [],
  is_meter_module: false,
  meter_config: { default_factor: 1.0, require_photo: true },
  location_meter_configs: [],
};
const emptyCol = { key: '', label: '', type: 'text', required: false, options: '' };

export default function InfoModuleManagement() {
  const [modules, setModules] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyModule);
  const [saving, setSaving] = useState(false);
  const [showPresets, setShowPresets] = useState(false);
  const [activatingPreset, setActivatingPreset] = useState(false);
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const fetchAll = async () => {
    try {
      const [mRes, lRes] = await Promise.all([
        axios.get(`${API}/api/info/modules`, { headers }),
        axios.get(`${API}/api/user/locations`, { headers }),
      ]);
      setModules(mRes.data || []);
      setLocations(lRes.data || []);
    } catch (e) {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const displayed = showAll ? modules : modules.filter(m => m.is_active);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyModule });
    setShowDialog(true);
  };

  const openEdit = (m) => {
    setEditing(m);
    setForm({
      name: m.name, type: m.type, icon: m.icon || 'FileText',
      visible_for_all_locations: m.visible_for_all_locations !== false,
      location_ids: m.location_ids || [],
      employee_can_add: m.employee_can_add !== false,
      employee_can_delete_own: !!m.employee_can_delete_own,
      table_schema: (m.table_schema || []).map(c => ({
        ...c, options: Array.isArray(c.options) ? c.options.join(',') : (c.options || ''),
      })),
      // Meter-Felder
      is_meter_module: !!m.is_meter_module,
      meter_config: m.meter_config || { default_factor: 1.0, require_photo: true },
      location_meter_configs: m.location_meter_configs || [],
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name ist Pflicht'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        table_schema: form.is_meter_module ? [] : form.table_schema.map(c => ({
          ...c,
          key: c.key || c.label.toLowerCase().replace(/\s+/g, '_'),
          options: c.type === 'select' && c.options
            ? c.options.split(',').map(o => o.trim()).filter(Boolean)
            : [],
        })),
        // Meter-Felder nur wenn Meter-Modul
        meter_config: form.is_meter_module ? form.meter_config : undefined,
        location_meter_configs: form.is_meter_module ? form.location_meter_configs : undefined,
      };
      if (editing) {
        await axios.put(`${API}/api/info/modules/${editing.id}`, payload, { headers });
        toast.success('Modul aktualisiert');
      } else {
        await axios.post(`${API}/api/info/modules`, payload, { headers });
        toast.success('Modul erstellt');
      }
      setShowDialog(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleActivateStromPreset = async () => {
    setActivatingPreset(true);
    try {
      await axios.post(`${API}/api/info/modules/preset-strom`, {}, { headers });
      toast.success('Strom-Modul aktiviert! Es erscheint jetzt in der Sidebar unter "Info".');
      fetchAll();
    } catch (e) {
      if (e.response?.status === 409) {
        toast.info('Ein Strom-Modul ist bereits aktiv.');
      } else {
        toast.error(e.response?.data?.detail || 'Fehler beim Aktivieren');
      }
    } finally {
      setActivatingPreset(false);
    }
  };

  const handleActivateGasPreset = async () => {
    setActivatingPreset(true);
    try {
      await axios.post(`${API}/api/info/modules/preset-gas`, {}, { headers });
      toast.success('Gas-Modul aktiviert! Es erscheint jetzt in der Sidebar unter "Info".');
      fetchAll();
    } catch (e) {
      if (e.response?.status === 409) {
        toast.info('Ein Gas-Modul ist bereits aktiv.');
      } else {
        toast.error(e.response?.data?.detail || 'Fehler beim Aktivieren');
      }
    } finally {
      setActivatingPreset(false);
    }
  };

  const handleActivateWaterPreset = async () => {
    setActivatingPreset(true);
    try {
      await axios.post(`${API}/api/info/modules/preset-water`, {}, { headers });
      toast.success('Wasser-Modul aktiviert! Es erscheint jetzt in der Sidebar unter "Info".');
      fetchAll();
    } catch (e) {
      if (e.response?.status === 409) {
        toast.info('Ein Wasser-Modul ist bereits aktiv.');
      } else {
        toast.error(e.response?.data?.detail || 'Fehler beim Aktivieren');
      }
    } finally {
      setActivatingPreset(false);
    }
  };

  const handleActivateAbscheiderPreset = async () => {
    setActivatingPreset(true);
    try {
      await axios.post(`${API}/api/info/modules/preset-abscheider`, {}, { headers });
      toast.success('Abscheider-Modul aktiviert! Es erscheint jetzt unter "Info".');
      fetchAll();
    } catch (e) {
      if (e.response?.status === 409) {
        toast.info('Ein Abscheider-Modul ist bereits aktiv.');
      } else {
        toast.error(e.response?.data?.detail || 'Fehler beim Aktivieren');
      }
    } finally {
      setActivatingPreset(false);
    }
  };

  const handleActivateAktionenPreset = async () => {
    setActivatingPreset(true);
    try {
      await axios.post(`${API}/api/info/modules/preset-aktionen`, {}, { headers });
      toast.success('Aktionen-Modul aktiviert! PDFs können jetzt hochgeladen werden.');
      fetchAll();
    } catch (e) {
      if (e.response?.status === 409) {
        toast.info('Ein Aktionen-Modul ist bereits vorhanden.');
      } else {
        toast.error(e.response?.data?.detail || 'Fehler beim Aktivieren');
      }
    } finally {
      setActivatingPreset(false);
    }
  };

  const handleArchive = async (m) => {
    if (!window.confirm(`"${m.name}" archivieren? Daten bleiben erhalten.`)) return;
    try {
      await axios.delete(`${API}/api/info/modules/${m.id}`, { headers });
      toast.success('Archiviert');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  const handleRestore = async (m) => {
    try {
      await axios.put(`${API}/api/info/modules/${m.id}`, { is_active: true }, { headers });
      toast.success('Wiederhergestellt');
      fetchAll();
    } catch (e) { toast.error('Fehler'); }
  };

  const moveOrder = async (m, dir) => {
    try {
      await axios.put(`${API}/api/info/modules/${m.id}`, { sort_order: m.sort_order + dir }, { headers });
      fetchAll();
    } catch { toast.error('Fehler'); }
  };

  const updateLocCfg = (locId, field, value) =>
    setForm(f => ({
      ...f,
      location_meter_configs: (() => {
        const exists = f.location_meter_configs.find(c => c.location_id === locId);
        if (exists) return f.location_meter_configs.map(c => c.location_id === locId ? { ...c, [field]: value } : c);
        return [...f.location_meter_configs, { location_id: locId, zaehler_nr_ht: '', zaehler_nr_nt: '', factor: '', [field]: value }];
      })(),
    }));

  const addColumn = () =>
    setForm(f => ({ ...f, table_schema: [...f.table_schema, { ...emptyCol }] }));

  const updateCol = (idx, field, val) =>
    setForm(f => ({
      ...f,
      table_schema: f.table_schema.map((c, i) => i === idx ? { ...c, [field]: val } : c),
    }));

  const removeCol = (idx) =>
    setForm(f => ({ ...f, table_schema: f.table_schema.filter((_, i) => i !== idx) }));

  const LucideIcon = ({ name, ...props }) => {
    const found = ICON_OPTIONS.find(o => o.value === name);
    if (found) return <found.Icon {...props} />;
    return <FileText {...props} />;
  };

  const hasStromModule = modules.some(m => m.is_meter_module && m.meter_type === 'strom' && m.is_active);
  const hasGasModule = modules.some(m => m.is_meter_module && m.meter_type === 'gas' && m.is_active);
  const hasWaterModule = modules.some(m => m.is_meter_module && m.meter_type === 'water' && m.is_active);
  const hasAbscheiderModule = modules.some(m => m.type === 'abscheider' && m.is_active);
  const hasAktionenModule = modules.some(m => m.type === 'aktionen' && m.is_active);

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto space-y-4 p-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">Info-Module verwalten</h1>
            <p className="text-sm text-muted-foreground">
              Erstelle und verwalte Einträge im Info-Menü der Sidebar.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowAll(s => !s)}>
              {showAll ? 'Nur aktive' : 'Alle anzeigen'}
            </Button>
            <Button size="sm" onClick={openCreate} data-testid="create-info-module-btn">
              <Plus className="h-4 w-4 mr-1" /> Neues Modul
            </Button>
          </div>
        </div>

        {/* Standard-Vorlagen – einklappbar, klar als OPTIONAL markiert */}
        <Card className="border-muted">
          <CardHeader
            className="pb-2 pt-4 cursor-pointer select-none"
            onClick={() => setShowPresets(v => !v)}
          >
            <CardTitle className="text-sm font-semibold flex items-center justify-between gap-2">
              <span className="flex items-center gap-2">
                <Zap className="h-4 w-4 text-amber-500" /> Optionale Vorlagen
                <span className="text-xs text-muted-foreground font-normal">(manuell aktivieren)</span>
              </span>
              <span className="text-xs text-muted-foreground">{showPresets ? '▲' : '▼'}</span>
            </CardTitle>
          </CardHeader>
          {showPresets && (
            <CardContent className="pb-4 space-y-3">
            {/* Strom */}
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="font-medium text-sm flex items-center gap-1.5">
                  <Zap className="h-4 w-4 text-yellow-500" /> Strom-Modul
                </div>
                <div className="text-xs text-muted-foreground">
                  HT 1.8.1 + NT 1.8.2, Wandlerfaktor, Foto-Pflicht, Anomalie-Erkennung, Sparkline.
                </div>
              </div>
              {hasStromModule ? (
                <Badge variant="secondary" className="text-xs whitespace-nowrap">✓ Bereits aktiv</Badge>
              ) : (
                <Button size="sm" onClick={handleActivateStromPreset} disabled={activatingPreset}
                  data-testid="activate-strom-preset-btn"
                  className="bg-amber-500 hover:bg-amber-600 text-white whitespace-nowrap">
                  {activatingPreset
                    ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Aktiviere…</>
                    : <><Zap className="h-3.5 w-3.5 mr-1" /> Strom aktivieren</>
                  }
                </Button>
              )}
            </div>

            {/* Gas */}
            <div className="flex items-center justify-between gap-3 pt-2 border-t">
              <div>
                <div className="font-medium text-sm flex items-center gap-1.5">
                  <Flame className="h-4 w-4 text-orange-500" /> Gas-Modul
                </div>
                <div className="text-xs text-muted-foreground">
                  m³-Ablesung, Brennwert × z-Zahl = kWh, optionaler Foto-Upload, Anomalie-Erkennung, Sparkline.
                </div>
              </div>
              {hasGasModule ? (
                <Badge variant="secondary" className="text-xs whitespace-nowrap">✓ Bereits aktiv</Badge>
              ) : (
                <Button size="sm" onClick={handleActivateGasPreset} disabled={activatingPreset}
                  data-testid="activate-gas-preset-btn"
                  className="bg-orange-500 hover:bg-orange-600 text-white whitespace-nowrap">
                  {activatingPreset
                    ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Aktiviere…</>
                    : <><Flame className="h-3.5 w-3.5 mr-1" /> Gas aktivieren</>
                  }
                </Button>
              )}
            </div>
            {/* Wasser */}
            <div className="flex items-center justify-between gap-3 pt-2 border-t">
              <div>
                <div className="font-medium text-sm flex items-center gap-1.5">
                  <Droplets className="h-4 w-4 text-blue-500" /> Wasser-Modul
                </div>
                <div className="text-xs text-muted-foreground">
                  m³-Ablesung, Liter-Anzeige, Haupt-/Zwischenzähler, Leckage-Alarm (30%), mehrere Zähler pro Standort.
                </div>
              </div>
              {hasWaterModule ? (
                <Badge variant="secondary" className="text-xs whitespace-nowrap">✓ Bereits aktiv</Badge>
              ) : (
                <Button size="sm" onClick={handleActivateWaterPreset} disabled={activatingPreset}
                  data-testid="activate-water-preset-btn"
                  className="bg-blue-500 hover:bg-blue-600 text-white whitespace-nowrap">
                  {activatingPreset
                    ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Aktiviere…</>
                    : <><Droplets className="h-3.5 w-3.5 mr-1" /> Wasser aktivieren</>
                  }
                </Button>
              )}
            </div>

            {/* Abscheider */}
            <div className="flex items-center justify-between gap-3 pt-2 border-t">
              <div>
                <div className="font-medium text-sm flex items-center gap-1.5">
                  <FlaskConical className="h-4 w-4 text-teal-600" /> Abscheider-Anlage
                </div>
                <div className="text-xs text-muted-foreground">
                  Digitales Entleerungs-Protokoll · SF/BA/KA-Mengen · Foto/PDF-Upload.
                </div>
              </div>
              {hasAbscheiderModule ? (
                <Badge variant="secondary" className="text-xs whitespace-nowrap">✓ Bereits aktiv</Badge>
              ) : (
                <Button size="sm" onClick={handleActivateAbscheiderPreset} disabled={activatingPreset}
                  data-testid="activate-abscheider-preset-btn"
                  className="bg-teal-600 hover:bg-teal-700 text-white whitespace-nowrap">
                  {activatingPreset
                    ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Aktiviere…</>
                    : <><FlaskConical className="h-3.5 w-3.5 mr-1" /> Abscheider aktivieren</>
                  }
                </Button>
              )}
            </div>

            {/* Aktions-Flyer */}
            <div className="flex items-center justify-between gap-3 pt-2 border-t">
              <div>
                <div className="font-medium text-sm flex items-center gap-1.5">
                  <Tag className="h-4 w-4 text-orange-500" /> Aktions-Flyer
                </div>
                <div className="text-xs text-muted-foreground">
                  PDF-Upload für Aktions-Flyer · Mitarbeiter sehen &amp; öffnen das PDF.
                </div>
              </div>
              {hasAktionenModule ? (
                <Badge variant="secondary" className="text-xs whitespace-nowrap">✓ Bereits aktiv</Badge>
              ) : (
                <Button size="sm" onClick={handleActivateAktionenPreset} disabled={activatingPreset}
                  data-testid="activate-aktionen-preset-btn"
                  className="bg-orange-500 hover:bg-orange-600 text-white whitespace-nowrap">
                  {activatingPreset
                    ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Aktiviere…</>
                    : <><Tag className="h-3.5 w-3.5 mr-1" /> Aktionen aktivieren</>
                  }
                </Button>
              )}
            </div>
          </CardContent>
          )}
        </Card>

        {/* Modul-Liste */}
        <div className="space-y-2">
          {loading && <p className="text-sm text-muted-foreground">Lade...</p>}
          {!loading && displayed.length === 0 && (
            <Card><CardContent className="py-8 text-center text-muted-foreground">
              Noch keine Module. Erstelle jetzt das erste.
            </CardContent></Card>
          )}
          {displayed.map((m, idx) => (
            <Card key={m.id} className={!m.is_active ? 'opacity-50' : ''}>
              <CardContent className="flex items-center gap-3 py-3">
                <div className="flex flex-col gap-0.5">
                  <Button variant="ghost" size="icon" className="h-5 w-5 p-0"
                    disabled={idx === 0} onClick={() => moveOrder(m, -1)}>
                    <ChevronUp className="h-3 w-3" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-5 w-5 p-0"
                    disabled={idx === displayed.length - 1} onClick={() => moveOrder(m, 1)}>
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                </div>

                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <div className="p-1.5 rounded bg-primary/10">
                    <LucideIcon name={m.icon} className="h-4 w-4 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="font-medium text-sm">{m.name}</span>
                      {m.is_builtin && (
                        <Badge variant="secondary" className="text-xs gap-1">
                          <Lock className="h-2.5 w-2.5" /> Eingebaut
                        </Badge>
                      )}
                      {m.is_meter_module && (
                        <Badge className="text-xs bg-amber-100 text-amber-700 border-amber-300 gap-1">
                          <Zap className="h-2.5 w-2.5" /> Zähler
                        </Badge>
                      )}
                      <Badge variant={m.type === 'pdf' ? 'outline' : 'default'} className="text-xs">
                        {m.type === 'pdf' ? 'PDF' : 'Tabelle'}
                      </Badge>
                      {!m.is_active && <Badge variant="destructive" className="text-xs">Archiviert</Badge>}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {m.visible_for_all_locations ? 'Alle Standorte' : `${(m.location_ids || []).length} Standort(e)`}
                      {m.type === 'table' && ` · ${(m.table_schema || []).length} Spalte(n)`}
                    </p>
                  </div>
                </div>

                <div className="flex gap-1 shrink-0">
                  <Button variant="ghost" size="icon" className="h-7 w-7"
                    onClick={() => openEdit(m)} data-testid={`edit-module-${m.id}`}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  {!m.is_builtin && (
                    m.is_active
                      ? <Button variant="ghost" size="icon" className="h-7 w-7 text-amber-600"
                          onClick={() => handleArchive(m)}>
                          <Archive className="h-3.5 w-3.5" />
                        </Button>
                      : <Button variant="ghost" size="icon" className="h-7 w-7 text-green-600"
                          onClick={() => handleRestore(m)}>
                          <ArchiveX className="h-3.5 w-3.5" />
                        </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Erstellen / Bearbeiten Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Modul bearbeiten' : 'Neues Info-Modul'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Name */}
            <div className="space-y-1">
              <label className="text-sm font-medium">Name *</label>
              <Input
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="z.B. Kassenvorgänge, Stromzähler..."
                data-testid="module-name-input"
              />
            </div>

            {/* Typ */}
            {!editing && (
              <div className="space-y-1">
                <label className="text-sm font-medium">Typ</label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: 'pdf', label: 'PDF-Ablage', desc: 'Dokumente hochladen (wie Planogramme)', Icon: FileText },
                    { value: 'table', label: 'Eingabe-Tabelle', desc: 'Mitarbeiter tragen Zeilen ein', Icon: Table2 },
                  ].map(({ value, label, desc, Icon }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setForm(f => ({ ...f, type: value }))}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${
                        form.type === value ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'
                      }`}
                    >
                      <Icon className="h-5 w-5 mb-1" />
                      <div className="font-medium text-sm">{label}</div>
                      <div className="text-xs text-muted-foreground">{desc}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Icon */}
            <div className="space-y-1">
              <label className="text-sm font-medium">Icon</label>
              <div className="flex gap-2 flex-wrap">
                {ICON_OPTIONS.map(({ value, label, Icon }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setForm(f => ({ ...f, icon: value }))}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-sm transition-all ${
                      form.icon === value ? 'border-primary bg-primary/10 text-primary' : 'border-border'
                    }`}
                  >
                    <Icon className="h-3.5 w-3.5" /> {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Sichtbarkeit */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Sichtbarkeit</label>
              <div className="flex items-center justify-between rounded-lg border px-3 py-2">
                <div>
                  <div className="text-sm font-medium">Alle Standorte</div>
                  <div className="text-xs text-muted-foreground">Modul für alle Standorte sichtbar</div>
                </div>
                <Switch
                  checked={form.visible_for_all_locations}
                  onCheckedChange={v => setForm(f => ({ ...f, visible_for_all_locations: v, location_ids: [] }))}
                />
              </div>
              {!form.visible_for_all_locations && locations.length > 0 && (
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground">Standorte auswählen:</label>
                  <div className="space-y-1 max-h-32 overflow-y-auto border rounded-md p-2">
                    {locations.map(loc => (
                      <label key={loc.id} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={form.location_ids.includes(loc.id)}
                          onChange={e => setForm(f => ({
                            ...f,
                            location_ids: e.target.checked
                              ? [...f.location_ids, loc.id]
                              : f.location_ids.filter(id => id !== loc.id)
                          }))}
                        />
                        {loc.name}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Typ B: Tabellen-Einstellungen */}
            {form.type === 'table' && (
              <>
                <div className="flex items-center justify-between rounded-lg border px-3 py-2">
                  <div className="text-sm font-medium">Mitarbeiter dürfen Zeilen hinzufügen</div>
                  <Switch
                    checked={form.employee_can_add}
                    onCheckedChange={v => setForm(f => ({ ...f, employee_can_add: v }))}
                  />
                </div>
                <div className="flex items-center justify-between rounded-lg border px-3 py-2">
                  <div className="text-sm font-medium">Mitarbeiter dürfen eigene Zeilen löschen</div>
                  <Switch
                    checked={form.employee_can_delete_own}
                    onCheckedChange={v => setForm(f => ({ ...f, employee_can_delete_own: v }))}
                  />
                </div>

                {/* Zähler-Modul Konfiguration */}
                {form.is_meter_module ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm font-medium text-amber-700">
                      <Zap className="h-4 w-4" /> Zähler-Konfiguration
                    </div>
                    <div className="flex items-center justify-between rounded-lg border px-3 py-2">
                      <div>
                        <div className="text-sm font-medium">Standard-Wandlerfaktor</div>
                        <div className="text-xs text-muted-foreground">Fallback wenn kein Standort-Faktor gesetzt</div>
                      </div>
                      <Input type="number" step="0.1" className="w-24 text-right"
                        value={form.meter_config?.default_factor || 1}
                        onChange={e => setForm(f => ({
                          ...f, meter_config: { ...f.meter_config, default_factor: parseFloat(e.target.value) || 1 }
                        }))} />
                    </div>
                    <div className="flex items-center justify-between rounded-lg border px-3 py-2">
                      <div className="text-sm font-medium">Foto-Nachweis Pflicht</div>
                      <Switch
                        checked={form.meter_config?.require_photo !== false}
                        onCheckedChange={v => setForm(f => ({
                          ...f, meter_config: { ...f.meter_config, require_photo: v }
                        }))} />
                    </div>
                    {/* Per-Standort-Konfiguration */}
                    <div className="space-y-1">
                      <label className="text-xs text-muted-foreground font-medium">Konfiguration pro Standort</label>
                      <p className="text-xs text-muted-foreground">
                        NT Zähler-Nr leer = Einzel-Tarif (nur HT)
                      </p>
                      <div className="rounded-lg border overflow-hidden">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="bg-muted/40 border-b">
                              <th className="text-left px-2 py-1.5 font-medium">Standort</th>
                              <th className="text-left px-1 py-1.5 font-medium">HT (1.8.1)</th>
                              <th className="text-left px-1 py-1.5 font-medium">NT (1.8.2)</th>
                              <th className="text-left px-1 py-1.5 font-medium w-16">Faktor</th>
                            </tr>
                          </thead>
                          <tbody>
                            {locations.map(loc => {
                              const cfg = form.location_meter_configs.find(c => c.location_id === loc.id) || { location_id: loc.id };
                              return (
                                <tr key={loc.id} className="border-b last:border-0">
                                  <td className="px-2 py-1 font-medium truncate max-w-[80px]">{loc.name}</td>
                                  <td className="px-1 py-0.5">
                                    <Input value={cfg.zaehler_nr_ht || ''}
                                      onChange={e => updateLocCfg(loc.id, 'zaehler_nr_ht', e.target.value)}
                                      placeholder="1.8.1-…" className="h-6 text-xs px-1.5" />
                                  </td>
                                  <td className="px-1 py-0.5">
                                    <Input value={cfg.zaehler_nr_nt || ''}
                                      onChange={e => updateLocCfg(loc.id, 'zaehler_nr_nt', e.target.value)}
                                      placeholder="leer=Einzel" className="h-6 text-xs px-1.5" />
                                  </td>
                                  <td className="px-1 py-0.5">
                                    <Input type="number" value={cfg.factor || ''}
                                      onChange={e => updateLocCfg(loc.id, 'factor', parseFloat(e.target.value) || '')}
                                      placeholder="50" className="h-6 text-xs px-1.5" />
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Standard Spalten-Schema */
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium">Tabellen-Spalten</label>
                      <Button type="button" variant="outline" size="sm" onClick={addColumn}>
                        <Plus className="h-3.5 w-3.5 mr-1" /> Spalte
                      </Button>
                    </div>
                    {form.table_schema.length === 0 && (
                      <p className="text-xs text-muted-foreground italic">Noch keine Spalten definiert.</p>
                    )}
                    {form.table_schema.map((col, idx) => (
                      <div key={idx} className="grid grid-cols-12 gap-1.5 items-start border rounded-md p-2 bg-muted/30">
                        <div className="col-span-4">
                          <Input
                            value={col.label}
                            onChange={e => updateCol(idx, 'label', e.target.value)}
                            placeholder="Bezeichnung"
                            className="h-8 text-xs"
                          />
                        </div>
                        <div className="col-span-3">
                          <select
                            value={col.type}
                            onChange={e => updateCol(idx, 'type', e.target.value)}
                            className="h-8 w-full rounded-md border bg-background px-2 text-xs"
                          >
                            {COL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                        </div>
                        {col.type === 'select' && (
                          <div className="col-span-3">
                            <Input
                              value={col.options || ''}
                              onChange={e => updateCol(idx, 'options', e.target.value)}
                              placeholder="Früh,Spät,Nacht"
                              className="h-8 text-xs"
                            />
                          </div>
                        )}
                        <div className={`flex items-center gap-1.5 ${col.type === 'select' ? 'col-span-2' : 'col-span-5'}`}>
                          <label className="flex items-center gap-1 text-xs cursor-pointer">
                            <input type="checkbox" checked={col.required} onChange={e => updateCol(idx, 'required', e.target.checked)} />
                            Pflicht
                          </label>
                          <Button type="button" variant="ghost" size="icon" className="h-6 w-6 ml-auto text-destructive"
                            onClick={() => removeCol(idx)}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Abbrechen</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-module-btn">
              {saving ? 'Speichern...' : 'Speichern'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
